/**
 * Per-person candidate pool — single source of truth for backend domain,
 * MongoDB persistence, and the admin wire DTO. A pool is a flat list of
 * `PreselectedCandidate`s that downstream pick-pipeline stages consume as hints.
 */

import { z } from "zod";
import { PersonIdSchema } from "./branded-id.schemas.js";

// === Per-candidate schemas ===

const preselectionTierSchema = z.enum([
  "triple-source",
  "high-marker",
  "wildcard",
  "peer",
  "fill",
]);

// A candidate's provenance is a list of the signals that fired for it, each
// carrying its own metadata beside its `type` discriminant. Meta-less signals
// (peer, distribution, salvaged) carry the base fields alone.
// `source` marks a signal as a reason the candidate is in the pool as opposed
// to one that only adjusts its score; `score` is what it contributed, and
// `value` is what the signal itself found, on its own 0–1 scale. All three sit
// on the base so every variant carries them — consumers never keep their own
// table of which type counts, and a recorded signal can always be weighed
// against its siblings.
//
// Both numbers are stored because neither survives being derived from the other:
// `score` is `value` times a weight that moves over time, so a row holding only
// `value` cannot reproduce the total it was part of, and a row holding only
// `score` cannot say how strongly the signal fired. `value` is also the only one
// of the two that means the same thing across signal types.
const signalBaseSchema = z.object({
  source: z.boolean(),
  value: z.number(),
  score: z.number(),
});

/**
 * An institution the pair was inside AT THE SAME TIME — the fact that makes a
 * suggestion between them a reintroduction rather than an introduction.
 *
 * Carried beside the familiarity score and not derived from it, because the two
 * answer different questions. Age changes how strongly an overlap predicts that they
 * still know each other (the score); it changes nothing about whether the overlap
 * happened (this). A decayed 2005 co-tenure is a weak prediction and a certain fact,
 * and the writer needs the fact (PLA-1419).
 *
 * One object rather than a name and a kind side by side: absent means "not
 * simultaneous, or nothing shared", and a half-populated reconnection has no meaning.
 */
export const FamiliarityReconnectionSchema = z.object({
  kind: z.enum(["employer", "school"]),
  /** Institution name as the profiles spell it, normalized only for matching. */
  institution: z.string(),
});

export type FamiliarityReconnection = z.infer<
  typeof FamiliarityReconnectionSchema
>;

export const CandidateSignalSchema = z.discriminatedUnion("type", [
  signalBaseSchema.extend({
    type: z.literal("cluster"),
    clusters: z.array(z.object({ lens: z.string(), clusterName: z.string() })),
  }),
  signalBaseSchema.extend({
    type: z.literal("marker"),
    shared: z.array(z.string()),
    totalShared: z.number(),
  }),
  signalBaseSchema.extend({
    type: z.literal("transactional"),
    targetType: z.string(),
    candidateType: z.string(),
    /**
     * Which two asks the score was computed from, as indices into each
     * person's `personCoreBrief.data.reasonsToMeet`. The score is a maximum
     * over every surviving ask-combination, so without these the deal cannot
     * be read back or judged.
     *
     * An identity, not a rank: the brief emits its reasons in no meaningful
     * order, so index 0 is not "their most important ask".
     *
     * Absent on pools written before PLA-1786, which never recorded them.
     */
    targetReasonIndex: z.number().int().optional(),
    candidateReasonIndex: z.number().int().optional(),
  }),
  signalBaseSchema.extend({
    type: z.literal("geo_proximity"),
    sharedMetro: z.string(),
  }),
  signalBaseSchema.extend({ type: z.literal("distribution") }),
  signalBaseSchema.extend({ type: z.literal("blind_spot") }),
  signalBaseSchema.extend({
    type: z.literal("familiarity"),
    // The pair's 0–100 familiarity strength, not the modifier: `score` is what
    // the signal contributed, this is the evidence it contributed it for. The
    // post-merge exclude reads it back off the candidate.
    familiarityScore: z.number(),
    /** Absent on pools written before reconnections were recorded (PLA-1419). */
    reconnection: FamiliarityReconnectionSchema.nullish(),
  }),
]);

export type CandidateSignal = z.infer<typeof CandidateSignalSchema>;
export type CandidateSignalType = CandidateSignal["type"];

/**
 * Signal types in declaration order, read off the union so a new variant joins
 * without a second list to update. Readers that lay signals out side by side
 * use this to give a type the same slot for every candidate, which is what lets
 * two candidates be compared by bar position instead of by reading labels.
 */
export const CANDIDATE_SIGNAL_TYPES: readonly CandidateSignalType[] =
  CandidateSignalSchema.options.map((variant) => variant.shape.type.value);

/** Zone for a pair the pool established nothing about. */
export const RELATIONSHIP_ZONE_STRANGER = "stranger";

/** Zone for a pair who were inside one institution at one time. */
export const RELATIONSHIP_ZONE_RECONNECTION = "reconnection";

/**
 * How the two already stand to each other, from whatever the pool established:
 * familiarity answers it when the pair were inside one institution at one time —
 * "reconnection", because that is the case a writer must not open as an introduction.
 *
 * `known_connection` used to answer first, but its only input was a host-supplied seed
 * list on `PersonInput` that nothing ever populated, so it made this a constant
 * "stranger" for the whole life of the field while familiarity sat beside it holding a
 * five-year shared employer (PLA-1419). Dropped in PLA-1658; the seed list itself went
 * in PLA-1890, leaving familiarity derived from profile overlap alone.
 */
export function resolveRelationshipZone(
  signals: readonly CandidateSignal[],
): string {
  const reconnected = signals.some(
    (signal) =>
      signal.type === "familiarity" && (signal.reconnection ?? null) !== null,
  );
  return reconnected
    ? RELATIONSHIP_ZONE_RECONNECTION
    : RELATIONSHIP_ZONE_STRANGER;
}

/**
 * The institution the pair were inside together, or null when the pool found none.
 *
 * Read by the S1 prompt's reconnection marker and by the persisted hint, so the
 * badge the model is shown and the flag the pick carries cannot drift apart — the
 * selection rule they both serve is stated in candidates-marked terms.
 */
export function resolveReconnection(
  signals: readonly CandidateSignal[],
): FamiliarityReconnection | null {
  const familiarity = signals.find((signal) => signal.type === "familiarity");
  return familiarity?.reconnection ?? null;
}

export const PreselectedCandidateSchema = z.object({
  personId: PersonIdSchema,
  score: z.number(),
  tier: preselectionTierSchema,
  signals: z.array(CandidateSignalSchema),
});

// === Per-person pool ===

export const PersonPoolSchema = z.object({
  candidates: z.array(PreselectedCandidateSchema),
});

// === Main Schema ===

export const CandidatePoolDataSchema = z.object({
  pools: z
    .record(z.string(), PersonPoolSchema)
    .describe("Map of personId to their scored candidate pool."),
});

export type CandidatePoolData = z.infer<typeof CandidatePoolDataSchema>;
export type PersonPool = z.infer<typeof PersonPoolSchema>;
export type PreselectedCandidate = z.infer<typeof PreselectedCandidateSchema>;
